/**
 * AnalyticsPage — Panel de métricas de agentes
 * Ruta: /analytics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell, PieChart, Pie, Legend,
  AreaChart, Area,
} from 'recharts';
import {
  MessageSquare, Star, Clock, RefreshCw, ChevronUp, ChevronDown,
  Users, TrendingUp, RotateCcw, CheckCircle, Filter, BarChart2,
  Bot, Zap, Euro, Moon, ArrowUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// ── Helpers ────────────────────────────────────────────────────
const PERIOD_OPTIONS = [
  { value: '7d', label: 'Últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'Todos los canales' },
  { value: 'chat_web', label: 'Chat Web' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
];

const CHANNEL_LABELS = {
  chat_web: 'Chat Web',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const CHANNEL_COLORS = {
  chat_web: '#6366f1',
  whatsapp: '#22c55e',
  email: '#f59e0b',
};

const STATUS_LABELS = {
  bot: 'Bot', waiting: 'Esperando', agent: 'Agente',
  pending: 'Pendiente', closed: 'Cerrada',
};
const STATUS_COLORS = {
  bot: '#6366f1', waiting: '#f59e0b', agent: '#22c55e',
  pending: '#a855f7', closed: '#64748b',
};

const SORT_KEYS = [
  { key: 'total_conversations', label: 'Conversaciones' },
  { key: 'avg_satisfaction', label: 'Satisfacción' },
  { key: 'avg_frt_ms', label: 'T. respuesta' },
  { key: 'resolution_rate', label: 'Resolución' },
];

function renderStars(rating) {
  if (!rating) return <span className="text-gray-600 text-xs">Sin valorar</span>;
  const stars = Math.round(rating);
  return (
    <span className="text-amber-400 text-sm">
      {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
      <span className="text-gray-400 text-xs ml-1">{rating.toFixed(1)}</span>
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = 'indigo', testid }) {
  const colorMap = {
    indigo: 'border-indigo-500/20 bg-indigo-500/5',
    green:  'border-green-500/20  bg-green-500/5',
    amber:  'border-amber-500/20  bg-amber-500/5',
    purple: 'border-purple-500/20 bg-purple-500/5',
    slate:  'border-slate-500/20  bg-slate-500/5',
  };
  const iconMap = {
    indigo: 'text-indigo-400', green: 'text-green-400',
    amber: 'text-amber-400', purple: 'text-purple-400', slate: 'text-slate-400',
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} className={iconMap[color]} />
        <p className="text-gray-400 text-xs font-medium">{label}</p>
      </div>
      <p className="text-white text-2xl font-bold">{value ?? '—'}</p>
      {sub && <p className="text-gray-500 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs shadow-xl">
      <p className="text-gray-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────
export default function AnalyticsPage() {
  const [period, setPeriod] = useState('30d');
  const [channel, setChannel] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [data, setData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('total_conversations');
  const [sortDir, setSortDir] = useState('desc');
  const [activeView, setActiveView] = useState('ai'); // 'ai' | 'team' | 'agents'

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period });
      if (channel) params.set('channel', channel);
      if (agentFilter) params.set('agent_id', agentFilter);
      const [r, rAi] = await Promise.all([
        api.get(`/api/analytics/agents?${params}`),
        api.get(`/api/analytics/ai-impact?period=${period}`),
      ]);
      setData(r.data);
      setAiData(rAi.data);
    } catch (e) {
      setError('Error al cargar métricas');
    } finally {
      setLoading(false);
    }
  }, [period, channel, agentFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedAgents = data?.agents ? [...data.agents].sort((a, b) => {
    const va = a[sortKey] ?? (sortDir === 'desc' ? -Infinity : Infinity);
    const vb = b[sortKey] ?? (sortDir === 'desc' ? -Infinity : Infinity);
    return sortDir === 'desc' ? vb - va : va - vb;
  }) : [];

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const team = data?.team || {};

  return (
    <div className="flex flex-col h-full bg-gray-950 text-white overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-950 z-10">
        <div className="flex items-center gap-3">
          <BarChart2 size={20} className="text-indigo-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Métricas de Agentes</h1>
            <p className="text-gray-500 text-xs">Rendimiento del equipo de soporte</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <select
            data-testid="period-select"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-lg px-3 py-1.5"
          >
            {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* Channel filter */}
          <select
            data-testid="channel-filter"
            value={channel}
            onChange={e => setChannel(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-lg px-3 py-1.5"
          >
            {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Button size="sm" variant="ghost" onClick={fetchData} disabled={loading}
            className="border border-gray-700 text-gray-400 hover:text-white text-xs" data-testid="refresh-btn">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-900/20 border border-red-700/30 rounded-lg px-4 py-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* View tabs */}
        <div className="flex gap-1 bg-gray-900 rounded-lg p-1 w-fit border border-gray-800">
          {[{ id: 'ai', label: 'Impacto IA', icon: Bot },
            { id: 'team', label: 'Equipo', icon: Users },
            { id: 'agents', label: 'Por agente', icon: BarChart2 }].map(v => (
            <button key={v.id} data-testid={`view-tab-${v.id}`}
              onClick={() => setActiveView(v.id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
                activeView === v.id
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-400 hover:text-gray-200'
              }`}>
              <v.icon size={12} /> {v.label}
            </button>
          ))}
        </div>

        {loading && !data && (
          <div className="flex items-center justify-center py-24 text-gray-500">
            <RefreshCw size={18} className="animate-spin mr-2" /> Cargando métricas...
          </div>
        )}

        {!loading && aiData && activeView === 'ai' && <AIImpactView data={aiData} period={period} />}
        {!loading && data && activeView === 'team' && <TeamView data={data} team={team} />}
        {!loading && data && activeView === 'agents' && (
          <AgentsView
            agents={sortedAgents}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            agentFilter={agentFilter}
            onAgentFilter={setAgentFilter}
          />
        )}
      </div>
    </div>
  );
}

// ── AI Impact View ─────────────────────────────────────────────
function AIImpactView({ data, period }) {
  const [editCost, setEditCost] = useState(false);
  const [costInput, setCostInput] = useState('');
  const [savingCost, setSavingCost] = useState(false);

  if (!data) return (
    <div className="flex items-center justify-center py-24 text-gray-500">
      <RefreshCw size={18} className="animate-spin mr-2" /> Cargando impacto IA...
    </div>
  );

  const { summary = {}, savings = {}, satisfaction = {}, off_hours = {}, trend = [], hero_text = '' } = data;

  const saveCost = async () => {
    const val = parseFloat(costInput);
    if (isNaN(val) || val <= 0) return;
    setSavingCost(true);
    try {
      await api.put('/api/chat/settings', { hourly_agent_cost: val });
      setEditCost(false);
    } catch {}
    setSavingCost(false);
  };

  const satData = [
    { label: 'IA', value: satisfaction.ai_avg, count: satisfaction.ai_count, color: '#6366f1' },
    { label: 'Humano', value: satisfaction.human_avg, count: satisfaction.human_count, color: '#22c55e' },
  ].filter(d => d.value !== null && d.value !== undefined);

  const donut = [
    { name: 'IA resolvió', value: summary.ai_resolved || 0, color: '#6366f1' },
    { name: 'Humano resolvió', value: summary.human_resolved || 0, color: '#22c55e' },
    { name: 'Escalado', value: summary.escalated || 0, color: '#f59e0b' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5" data-testid="ai-impact-view">
      {/* Hero card */}
      <div data-testid="ai-hero-card"
        className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-950 border border-indigo-700/30 shadow-xl">
        {/* decorative blobs */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <Bot size={22} className="text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">Impacto de la IA</span>
              {summary.ai_rate > 50 && (
                <span className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full flex items-center gap-1">
                  <ArrowUp size={9} /> Rendimiento alto
                </span>
              )}
            </div>
            <p className="text-white text-sm md:text-base leading-relaxed font-light max-w-3xl" data-testid="hero-text">
              {hero_text}
            </p>
          </div>
        </div>

        {/* Mini KPIs inside hero */}
        <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10">
            <p className="text-indigo-300 text-xs mb-1">Resueltas por IA</p>
            <p className="text-white text-xl font-bold">{summary.ai_resolved || 0}</p>
            <p className="text-indigo-400 text-xs">{summary.ai_rate || 0}% del total</p>
          </div>
          <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10">
            <p className="text-green-300 text-xs mb-1">Tiempo ahorrado</p>
            <p className="text-white text-xl font-bold">{savings.saved_hours_formatted || '—'}</p>
            <p className="text-green-400 text-xs">≈{savings.saved_cost_eur || 0}€ ahorrados</p>
          </div>
          <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10">
            <p className="text-amber-300 text-xs mb-1">Satisfacción IA</p>
            <p className="text-white text-xl font-bold">
              {satisfaction.ai_avg ? `${satisfaction.ai_avg.toFixed(1)}/5` : '—'}
            </p>
            <p className="text-amber-400 text-xs">{satisfaction.ai_count || 0} valoraciones</p>
          </div>
          <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10">
            <p className="text-purple-300 text-xs mb-1">Fuera de horario</p>
            <p className="text-white text-xl font-bold">{off_hours.total_conversations || 0}</p>
            <p className="text-purple-400 text-xs">convs. atendidas</p>
          </div>
        </div>
      </div>

      {/* Main metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Coste ahorrado card */}
        <div className="bg-gray-900 rounded-xl border border-green-500/20 p-5" data-testid="savings-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Euro size={15} className="text-green-400" />
              <p className="text-gray-400 text-xs font-medium">Coste ahorrado</p>
            </div>
            <button onClick={() => { setEditCost(true); setCostInput(String(data.hourly_agent_cost || 15)); }}
              className="text-[10px] text-gray-500 hover:text-gray-300 border border-gray-700 px-1.5 py-0.5 rounded">
              {data.hourly_agent_cost || 15}€/h ✎
            </button>
          </div>
          {editCost ? (
            <div className="flex gap-2 items-center mb-2">
              <input type="number" min="1" step="0.5"
                value={costInput}
                onChange={e => setCostInput(e.target.value)}
                className="bg-gray-800 border border-gray-600 text-white text-xs rounded px-2 py-1 w-20"
                placeholder="€/h" />
              <button onClick={saveCost} disabled={savingCost}
                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded">
                {savingCost ? '...' : 'OK'}
              </button>
              <button onClick={() => setEditCost(false)} className="text-gray-500 hover:text-gray-300 text-xs">✕</button>
            </div>
          ) : null}
          <p className="text-white text-3xl font-bold">{savings.saved_cost_eur || 0}<span className="text-green-400 text-lg ml-1">€</span></p>
          <p className="text-gray-500 text-xs mt-1">{savings.saved_hours_formatted || '—'} de trabajo del equipo</p>
          {savings.used_default_time && (
            <p className="text-amber-500/70 text-[10px] mt-1">* Usando 8 min por conv. (sin datos reales de resolución humana aún)</p>
          )}
        </div>

        {/* Tasa de resolución IA */}
        <div className="bg-gray-900 rounded-xl border border-indigo-500/20 p-5" data-testid="ai-rate-card">
          <div className="flex items-center gap-2 mb-3">
            <Bot size={15} className="text-indigo-400" />
            <p className="text-gray-400 text-xs font-medium">Tasa de resolución IA</p>
          </div>
          <p className="text-white text-3xl font-bold">{summary.ai_rate || 0}<span className="text-indigo-400 text-lg ml-0.5">%</span></p>
          <div className="mt-2 bg-gray-800 rounded-full h-1.5">
            <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(summary.ai_rate || 0, 100)}%` }} />
          </div>
          <p className="text-gray-500 text-xs mt-1.5">{summary.ai_resolved || 0} de {summary.total_conversations || 0} conversaciones</p>
        </div>

        {/* Tasa de escalado */}
        <div className="bg-gray-900 rounded-xl border border-amber-500/20 p-5" data-testid="escalation-card">
          <div className="flex items-center gap-2 mb-3">
            <Users size={15} className="text-amber-400" />
            <p className="text-gray-400 text-xs font-medium">Tasa de escalado</p>
          </div>
          <p className="text-white text-3xl font-bold">{summary.escalated_rate || 0}<span className="text-amber-400 text-lg ml-0.5">%</span></p>
          <p className="text-gray-500 text-xs mt-1.5">{summary.escalated || 0} transferidas a humano</p>
          <div className="mt-2 space-y-1 text-xs text-gray-500">
            <p>• Humano resolvió: <span className="text-green-400">{summary.human_resolved || 0}</span></p>
            <p>• Auto-cerradas: <span className="text-gray-400">{summary.auto_closed || 0}</span></p>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tendencia mensual */}
        <div className="md:col-span-2 bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="ai-trend-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={14} className="text-indigo-400" />
            Tendencia mensual — Tasa de resolución IA (%)
          </h3>
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="aiGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} unit="%" domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />}
                  formatter={(v, n) => [n === 'ai_rate' ? `${v}%` : v, n === 'ai_rate' ? 'Tasa IA' : 'Convs.']} />
                <Area type="monotone" dataKey="ai_rate" name="ai_rate"
                  stroke="#6366f1" strokeWidth={2} fill="url(#aiGrad)" dot={{ r: 4, fill: '#6366f1' }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-600 text-xs">
              Sin datos de tendencia aún (mínimo 1 mes de datos)
            </div>
          )}
        </div>

        {/* Distribución resuelta por */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="resolution-donut">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <CheckCircle size={14} className="text-green-400" />
            Resolución por tipo
          </h3>
          {donut.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={donut} dataKey="value" nameKey="name"
                  cx="50%" cy="45%" outerRadius={65} innerRadius={35}>
                  {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-600 text-xs">Sin datos</div>
          )}
          <div className="mt-2 space-y-1">
            {donut.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  <span className="text-gray-400">{d.name}</span>
                </span>
                <span className="text-white font-medium">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Satisfaction comparison + Off-hours */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* IA vs Humano satisfaction */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="sat-comparison-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <Star size={14} className="text-amber-400" />
            Satisfacción: IA vs Humano
          </h3>
          {satData.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={satData} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <YAxis domain={[0, 5]} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <Tooltip formatter={(v, n) => [`${v}/5`, n === 'value' ? 'Puntuación media' : n]} />
                <Bar dataKey="value" name="Puntuación" radius={[6, 6, 0, 0]}>
                  {satData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-gray-600 text-xs">Sin valoraciones registradas aún</div>
          )}
          {satData.length > 0 && (
            <div className="mt-3 flex gap-4 text-xs">
              {satData.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                  <span className="text-gray-400">{d.label}: </span>
                  <span className="text-white font-medium">{d.value?.toFixed(1)}/5</span>
                  <span className="text-gray-600">({d.count} val.)</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Off-hours coverage */}
        <div className="bg-gray-900 rounded-xl border border-purple-500/20 p-5" data-testid="off-hours-card">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <Moon size={14} className="text-purple-400" />
            Cobertura fuera de horario
          </h3>
          <div className="flex items-end gap-4 mb-4">
            <div>
              <p className="text-gray-400 text-xs mb-1">Conversaciones fuera de horario</p>
              <p className="text-white text-3xl font-bold">{off_hours.total_conversations || 0}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-1">Atendidas por IA</p>
              <p className="text-purple-300 text-2xl font-bold">{off_hours.ai_handled || 0}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-1">Cobertura</p>
              <p className="text-green-400 text-2xl font-bold">{off_hours.coverage_rate || 0}%</p>
            </div>
          </div>
          <div className="bg-gray-800 rounded-full h-2">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full"
              style={{ width: `${Math.min(off_hours.coverage_rate || 0, 100)}%` }} />
          </div>
          <p className="text-gray-600 text-xs mt-3">
            La IA atendió conversaciones fuera del horario laboral configurado, garantizando respuesta 24/7.
          </p>
        </div>
      </div>
    </div>
  );
}


// ── Team View ──────────────────────────────────────────────────
function TeamView({ data, team }) {
  const channels = (data.channels || []).map(c => ({
    ...c,
    label: CHANNEL_LABELS[c.channel] || c.channel,
    fill: CHANNEL_COLORS[c.channel] || '#6366f1',
  }));

  const statuses = (data.statuses || []).map(s => ({
    ...s,
    label: STATUS_LABELS[s.status] || s.status,
    fill: STATUS_COLORS[s.status] || '#64748b',
  }));

  const trendData = (data.trend || []);
  const satDist = (data.satisfaction_distribution || []);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <MetricCard icon={MessageSquare} label="Conversaciones" color="indigo"
          value={team.total_conversations?.toLocaleString() || '0'}
          sub={`${team.closed_conversations || 0} cerradas`}
          testid="kpi-total-convs" />
        <MetricCard icon={Clock} label="T. primera respuesta" color="amber"
          value={team.avg_frt_formatted || '—'}
          sub="Tiempo medio del equipo"
          testid="kpi-avg-frt" />
        <MetricCard icon={CheckCircle} label="T. resolución" color="green"
          value={team.avg_resolution_formatted || '—'}
          sub={`${team.resolution_rate || 0}% de resolución`}
          testid="kpi-avg-resolution" />
        <MetricCard icon={Star} label="Satisfacción media" color="purple"
          value={team.avg_satisfaction ? `${team.avg_satisfaction.toFixed(1)}/5` : '—'}
          sub={`${team.total_rated || 0} valoraciones (${team.satisfaction_rate || 0}%)`}
          testid="kpi-avg-satisfaction" />
        <MetricCard icon={MessageSquare} label="Tasa de resolución" color="green"
          value={`${team.resolution_rate || 0}%`}
          sub={`${team.closed_conversations || 0} conversaciones resueltas`}
          testid="kpi-resolution-rate" />
        <MetricCard icon={RotateCcw} label="Tasa de reapertura" color="amber"
          value={`${team.reopen_rate || 0}%`}
          sub={`${team.reopen_count || 0} reapertura(s) en periodo`}
          testid="kpi-reopen-rate" />
        <MetricCard icon={Users} label="Agentes" color="indigo"
          value={(data.agents || []).length}
          sub="Agentes con conversaciones asignadas"
          testid="kpi-agents-count" />
        <MetricCard icon={TrendingUp} label="Conversaciones cerradas" color="slate"
          value={team.closed_conversations?.toLocaleString() || '0'}
          sub={`de ${team.total_conversations || 0} totales`}
          testid="kpi-closed" />
      </div>

      {/* Charts row 1: FRT trend + Satisfaction distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* FRT & resolution trend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="trend-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={14} className="text-indigo-400" />
            Tendencia semanal
          </h3>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} unit="s" />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
                <Line type="monotone" dataKey="avg_frt_seconds" name="T. Respuesta (s)"
                  stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="avg_resolution_seconds" name="T. Resolución (s)"
                  stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-600 text-xs">
              Sin datos de tendencia aún
            </div>
          )}
        </div>

        {/* Satisfaction distribution */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="satisfaction-dist-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <Star size={14} className="text-amber-400" />
            Distribución de satisfacción
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={satDist} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="stars" tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={v => '★'.repeat(v)} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />}
                formatter={(v) => [v, 'Valoraciones']}
                labelFormatter={(l) => `${'★'.repeat(l)} (${l}/5)`} />
              <Bar dataKey="count" name="Valoraciones" radius={[4, 4, 0, 0]}>
                {satDist.map((_, i) => (
                  <Cell key={i} fill={['#ef4444','#f97316','#eab308','#84cc16','#22c55e'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2: channels + statuses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Channels */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="channels-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <MessageSquare size={14} className="text-indigo-400" />
            Conversaciones por canal
          </h3>
          {channels.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={channels} layout="vertical" barSize={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis type="category" dataKey="label" tick={{ fill: '#9ca3af', fontSize: 11 }} width={72} />
                <Tooltip content={<CustomTooltip />} formatter={(v) => [v, 'Conversaciones']} />
                <Bar dataKey="count" name="Conversaciones" radius={[0, 4, 4, 0]}>
                  {channels.map((c, i) => <Cell key={i} fill={c.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-600 text-xs">Sin datos de canal</div>
          )}
        </div>

        {/* Statuses */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5" data-testid="statuses-chart">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
            <Filter size={14} className="text-green-400" />
            Conversaciones por estado
          </h3>
          {statuses.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={statuses} dataKey="count" nameKey="label"
                  cx="50%" cy="50%" outerRadius={70} label={({ label, percent }) =>
                    `${label} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false} fontSize={10} fill="#6366f1">
                  {statuses.map((s, i) => <Cell key={i} fill={s.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => [v, 'Conversaciones']} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-600 text-xs">Sin datos de estado</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Agents View ────────────────────────────────────────────────
function AgentsView({ agents, sortKey, sortDir, onSort, agentFilter, onAgentFilter }) {
  const SortIcon = ({ k }) => (
    sortKey === k
      ? (sortDir === 'desc' ? <ChevronDown size={11} /> : <ChevronUp size={11} />)
      : <ChevronDown size={11} className="opacity-30" />
  );

  return (
    <div className="space-y-5" data-testid="agents-view">
      {/* Top agent cards */}
      {agents.slice(0, 3).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {agents.slice(0, 3).map((a, i) => (
            <div key={a.agent_id} data-testid={`agent-card-${i}`}
              className={`bg-gray-900 rounded-xl border p-4 ${
                i === 0 ? 'border-amber-500/30' : 'border-gray-800'
              }`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i === 0 ? 'bg-amber-500 text-black' : i === 1 ? 'bg-gray-500 text-white' : 'bg-amber-800/60 text-amber-300'
                }`}>
                  {i + 1}
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{a.name}</p>
                  <p className="text-gray-500 text-xs">{a.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><p className="text-gray-500">Convs.</p><p className="text-white font-medium">{a.total_conversations}</p></div>
                <div><p className="text-gray-500">Satisf.</p>{renderStars(a.avg_satisfaction)}</div>
                <div><p className="text-gray-500">T. Respuesta</p><p className="text-amber-400 font-medium">{a.avg_frt_formatted}</p></div>
                <div><p className="text-gray-500">Resolución</p><p className="text-green-400 font-medium">{a.resolution_rate}%</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comparison table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden" data-testid="agents-table">
        <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Ranking de agentes</h3>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs">Ordenar por:</span>
            <div className="flex gap-1">
              {SORT_KEYS.map(sk => (
                <button key={sk.key} onClick={() => onSort(sk.key)}
                  data-testid={`sort-${sk.key}`}
                  className={`text-xs px-2 py-1 rounded-md transition-colors ${
                    sortKey === sk.key
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                  }`}>
                  {sk.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-800 text-gray-500">
                <th className="text-left px-5 py-3 font-medium">Agente</th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:text-gray-300" onClick={() => onSort('total_conversations')}>
                  <span className="flex items-center justify-end gap-1">Convs <SortIcon k="total_conversations" /></span>
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:text-gray-300" onClick={() => onSort('resolution_rate')}>
                  <span className="flex items-center justify-end gap-1">Resolución <SortIcon k="resolution_rate" /></span>
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:text-gray-300" onClick={() => onSort('avg_frt_ms')}>
                  <span className="flex items-center justify-end gap-1">T. Respuesta <SortIcon k="avg_frt_ms" /></span>
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:text-gray-300" onClick={() => onSort('avg_resolution_ms')}>
                  <span className="flex items-center justify-end gap-1">T. Resolución <SortIcon k="avg_resolution_ms" /></span>
                </th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer hover:text-gray-300" onClick={() => onSort('avg_satisfaction')}>
                  <span className="flex items-center justify-end gap-1">Satisfacción <SortIcon k="avg_satisfaction" /></span>
                </th>
                <th className="text-right px-4 py-3 font-medium">Reapertura</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-600">
                  Sin datos de agentes en el periodo seleccionado
                </td></tr>
              ) : agents.map((a, i) => (
                <tr key={a.agent_id} data-testid={`agent-row-${i}`}
                  className={`border-b border-gray-800/50 transition-colors hover:bg-gray-800/40 ${
                    agentFilter === a.agent_id ? 'bg-indigo-500/5' : ''
                  }`}
                  onClick={() => onAgentFilter(agentFilter === a.agent_id ? '' : a.agent_id)}
                  style={{ cursor: 'pointer' }}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-indigo-600/30 flex items-center justify-center text-indigo-400 font-bold text-xs">
                        {(a.name || 'A')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{a.name}</p>
                        <p className="text-gray-500">{a.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-200">{a.total_conversations}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${
                      a.resolution_rate >= 80 ? 'text-green-400' :
                      a.resolution_rate >= 50 ? 'text-amber-400' : 'text-red-400'
                    }`}>{a.resolution_rate}%</span>
                  </td>
                  <td className="px-4 py-3 text-right text-amber-400 font-medium">{a.avg_frt_formatted}</td>
                  <td className="px-4 py-3 text-right text-indigo-300 font-medium">{a.avg_resolution_formatted}</td>
                  <td className="px-4 py-3 text-right">{renderStars(a.avg_satisfaction)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={a.reopen_rate > 10 ? 'text-red-400' : 'text-gray-400'}>
                      {a.reopen_rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
